import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserContext } from '../../contexts';
import { identifyUser, trackOrderCompleted } from '../../lib/analytics';
import { captureUtmParams, getAttributionData, getAttributionMetadata } from '../../lib/attribution';
import { claimHostedPurchase } from '../../lib/hostedPurchase';
import { isStaffAnalyticsSession } from '../../lib/staffAnalytics';
import { useRetrieveCheckout } from '../ActivatePlan/queries';
import Card from '../Card';
import PortalLayout from '../PortalLayout';
import Spinner from '../Spinner';
import Text from '../Text';
import TextLink from '../TextLink';
import { useRecordCheckoutConversion } from './mutations';

const BUYER_POLL_LIMIT_MS = 5 * 60 * 1000;

export function CheckoutSuccessful() {
  const { isLoggedIn } = useUserContext();
  const location = useLocation();
  const hasTrackedRef = useRef(false);
  const hasRecordedConversionRef = useRef(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [recordCheckoutConversion] = useRecordCheckoutConversion();

  // Get session_id from URL params (Stripe passes this on redirect)
  const sessionId = new URLSearchParams(location.search).get('session_id');

  const { data, loading, startPolling, stopPolling } = useRetrieveCheckout({
    variables: { checkoutSessionId: sessionId || '' },
    skip: !sessionId,
  });

  const checkoutData = data?.retrieveCheckout;
  const orderId = checkoutData?.paymentIntentId || checkoutData?.invoiceId;
  const readyToTrack = Boolean(checkoutData?.paymentCompleted && checkoutData.userId && orderId);
  // A paid order with no user yet can still appear after the Meta conversion timeout.
  const waitingForBuyer = Boolean(checkoutData?.paymentCompleted && orderId && !checkoutData.userId);
  // The server resolves the buyer itself, so a slow webhook only delays the conversion until the
  // poll gives up; waiting for the user ID keeps it on the server event in the usual case.
  const readyToRecordConversion = Boolean(
    checkoutData?.paymentCompleted && (checkoutData.userId || pollTimedOut),
  );

  // Stripe can redirect before the payment completes. Record the server
  // conversion after 30s, but keep polling a paid order for a while so a buyer
  // who appears meanwhile still produces the one browser purchase. A guest has
  // no user until signup, where plan activation sends the purchase instead.
  useEffect(() => {
    if (!sessionId || readyToTrack || isStaffAnalyticsSession()) return;
    startPolling(2000);
    const timeout = window.setTimeout(() => {
      setPollTimedOut(true);
    }, 30000);
    const pollLimit = window.setTimeout(stopPolling, BUYER_POLL_LIMIT_MS);
    return () => {
      window.clearTimeout(timeout);
      window.clearTimeout(pollLimit);
      stopPolling();
    };
  }, [sessionId, readyToTrack, startPolling, stopPolling]);

  useEffect(() => {
    if (!pollTimedOut || readyToTrack || waitingForBuyer || isStaffAnalyticsSession()) return;
    stopPolling();
  }, [pollTimedOut, readyToTrack, waitingForBuyer, stopPolling]);

  // Track Order Completed event once when we have the data
  useEffect(() => {
    captureUtmParams();
    if (hasTrackedRef.current || !checkoutData || !sessionId || !readyToTrack || !orderId) return;
    if (isStaffAnalyticsSession()) return;

    const { plan, amountTotal, userId, email } = checkoutData;
    if (!userId || !claimHostedPurchase(sessionId, orderId)) {
      hasTrackedRef.current = true;
      return;
    }

    identifyUser(String(userId), {
      email,
      ...getAttributionData(),
    });

    // transaction_id is orderId (payment intent, otherwise invoice).
    trackOrderCompleted({
      userId: userId!,
      invoiceId: orderId,
      checkoutSessionId: sessionId,
      amountPaid: amountTotal,
      checkoutSource: 'stripe_checkout',
      analyticsProducts: [
        {
          name: plan,
          product_id: plan,
          price: amountTotal,
          quantity: 1,
        },
      ],
    });

    hasTrackedRef.current = true;
  }, [checkoutData, sessionId, readyToTrack, orderId]);

  // The API stores consent and click IDs on the Stripe payment and sends the
  // server-side purchase; Meta dedupes it with the browser event above.
  useEffect(() => {
    if (hasRecordedConversionRef.current || !sessionId || !readyToRecordConversion) return;
    if (isStaffAnalyticsSession()) return;

    hasRecordedConversionRef.current = true;
    const { gclid, gbraid, wbraid, fbclid, marketingAnonymousId } = getAttributionMetadata();
    recordCheckoutConversion({
      variables: { checkoutSessionId: sessionId, gclid, gbraid, wbraid, fbclid, marketingAnonymousId },
      context: { silent: true },
    }).catch(() => undefined);
  }, [sessionId, readyToRecordConversion, recordCheckoutConversion]);

  if (sessionId && loading) {
    return (
      <PortalLayout className="flex flex-col items-center">
        <Spinner className="mt-24" />
      </PortalLayout>
    );
  }

  return (
    <PortalLayout className="flex flex-col items-center">
      <Card className="mt-12 w-152">
        <div className="flex flex-col items-center p-16">
          <Text variant="success">
            <span className="material-icons text-5xl">check_circle</span>
          </Text>
          <p className={classNames(`text-3xl text-center my-8`, isLoggedIn ? 'w-112' : 'w-72')}>
            {isLoggedIn ? 'Thank you for your purchase' : 'Please check your email to get started'}
          </p>
          <Text variant="muted">
            <p className="text-lg">
              {isLoggedIn
                ? 'We have sent you an email with an activation link'
                : 'Thank you for your purchase'}
            </p>
          </Text>
          {isLoggedIn && (
            <TextLink to="/home" className="mt-16">
              Open Tax Dashboard
            </TextLink>
          )}
        </div>
      </Card>
    </PortalLayout>
  );
}

export default React.memo(CheckoutSuccessful);
